import { useEffect, useState } from 'react';
import ColorThief from 'colorthief';

const FLEX_THEME_CACHE = new Map();

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function toHex(value) {
  return clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');
}

function rgbToHex(rgb) {
  return `#${toHex(rgb[0])}${toHex(rgb[1])}${toHex(rgb[2])}`;
}

function luminance(rgb) {
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

function mixRgb(left, right, weight) {
  const w = clamp(weight, 0, 1);
  return [
    left[0] * (1 - w) + right[0] * w,
    left[1] * (1 - w) + right[1] * w,
    left[2] * (1 - w) + right[2] * w,
  ];
}

function rgbToHsl(rgb) {
  const nr = rgb[0] / 255;
  const ng = rgb[1] / 255;
  const nb = rgb[2] / 255;
  const max = Math.max(nr, ng, nb);
  const min = Math.min(nr, ng, nb);
  const lightness = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) return { hue: 0, saturation: 0, lightness };

  const saturation = lightness > 0.5
    ? delta / (2 - max - min)
    : delta / (max + min);

  let hue = 0;
  if (max === nr) hue = ((ng - nb) / delta + (ng < nb ? 6 : 0)) * 60;
  else if (max === ng) hue = ((nb - nr) / delta + 2) * 60;
  else hue = ((nr - ng) / delta + 4) * 60;

  return { hue, saturation, lightness };
}

function colorDistanceSq(left, right) {
  const dr = left[0] - right[0];
  const dg = left[1] - right[1];
  const db = left[2] - right[2];
  return dr * dr + dg * dg + db * db;
}

function pickSideColors(palette, centerColor) {
  const normalized = (Array.isArray(palette) ? palette : [])
    .filter((rgb) => Array.isArray(rgb) && rgb.length === 3)
    .sort((a, b) => colorDistanceSq(b, centerColor) - colorDistanceSq(a, centerColor));

  const sideA = normalized[0] || centerColor;
  const sideB = normalized[1] || mixRgb(centerColor, [255, 255, 255], 0.2);
  return { sideA, sideB };
}

function pickAccentColor(centerColor, sideA, sideB) {
  const candidates = [centerColor, sideA, sideB];
  let best = candidates[0];
  let bestScore = -1;

  candidates.forEach((rgb) => {
    const { saturation, lightness } = rgbToHsl(rgb);
    const score = saturation * (1 - Math.abs(lightness - 0.55));
    if (score > bestScore) {
      best = rgb;
      bestScore = score;
    }
  });

  return best;
}

function buildFlexibleVars(centerColor, sideA, sideB) {
  const isLightCenter = luminance(centerColor) >= 160;
  const accent = pickAccentColor(centerColor, sideA, sideB);

  if (isLightCenter) {
    const bg = mixRgb(centerColor, [255, 255, 255], 0.8);
    const bgSurface = mixRgb(centerColor, [255, 255, 255], 0.68);
    const primary = mixRgb(centerColor, sideA, 0.3);
    const secondary = mixRgb(centerColor, sideB, 0.3);
    const tertiary = mixRgb(sideA, sideB, 0.5);
    const deep = mixRgb(centerColor, [0, 0, 0], 0.48);
    const text = mixRgb(centerColor, [20, 24, 32], 0.86);
    const textSecondary = mixRgb(text, bg, 0.42);
    return {
      '--radio-primary': rgbToHex(primary),
      '--radio-secondary': rgbToHex(secondary),
      '--radio-tertiary': rgbToHex(tertiary),
      '--radio-accent': rgbToHex(accent),
      '--radio-deep': rgbToHex(deep),
      '--radio-bg': rgbToHex(bg),
      '--radio-bg-surface': rgbToHex(bgSurface),
      '--radio-text': rgbToHex(text),
      '--radio-text-secondary': rgbToHex(textSecondary),
      '--radio-playing-color': rgbToHex(text),
      '--radio-playing-shadow': 'rgba(255, 255, 255, 0.35)',
      '--radio-border': 'rgba(30, 48, 72, 0.28)',
      '--radio-shadow': 'rgba(22, 28, 38, 0.22)',
      '--radio-shadow-light': 'rgba(255, 255, 255, 0.55)',
      '--radio-memory-box-bg': `linear-gradient(180deg, ${rgbToHex(mixRgb(bg, [255, 255, 255], 0.15))} 0%, ${rgbToHex(bgSurface)} 100%)`,
      '--radio-memory-box-overlay': 'repeating-linear-gradient(-12deg, rgba(255, 255, 255, 0.07) 0 2px, rgba(0, 0, 0, 0.04) 2px 6px)',
      '--radio-device-screen-bg': `linear-gradient(180deg, ${rgbToHex(mixRgb(bg, [255, 255, 255], 0.15))} 0%, ${rgbToHex(bgSurface)} 100%)`,
    };
  }

  const bg = mixRgb(centerColor, [0, 0, 0], 0.74);
  const bgSurface = mixRgb(centerColor, [0, 0, 0], 0.6);
  const primary = mixRgb(centerColor, sideA, 0.26);
  const secondary = mixRgb(centerColor, sideB, 0.26);
  const tertiary = mixRgb(sideA, sideB, 0.5);
  const deep = mixRgb(centerColor, [0, 0, 0], 0.84);
  const text = mixRgb(centerColor, [245, 248, 255], 0.82);
  const textSecondary = mixRgb(text, bg, 0.46);
  return {
    '--radio-primary': rgbToHex(primary),
    '--radio-secondary': rgbToHex(secondary),
    '--radio-tertiary': rgbToHex(tertiary),
    '--radio-accent': rgbToHex(accent),
    '--radio-deep': rgbToHex(deep),
    '--radio-bg': rgbToHex(bg),
    '--radio-bg-surface': rgbToHex(bgSurface),
    '--radio-text': rgbToHex(text),
    '--radio-text-secondary': rgbToHex(textSecondary),
    '--radio-playing-color': rgbToHex(text),
    '--radio-border': 'rgba(175, 190, 218, 0.3)',
    '--radio-shadow': 'rgba(0, 0, 0, 0.58)',
    '--radio-shadow-light': 'rgba(255, 255, 255, 0.08)',
    '--radio-memory-box-bg': `linear-gradient(180deg, ${rgbToHex(mixRgb(bgSurface, [255, 255, 255], 0.04))} 0%, ${rgbToHex(bg)} 100%)`,
    '--radio-memory-box-overlay': 'repeating-linear-gradient(-12deg, rgba(255, 255, 255, 0.025) 0 2px, rgba(0, 0, 0, 0.12) 2px 6px)',
    '--radio-device-screen-bg': `linear-gradient(180deg, ${rgbToHex(mixRgb(bgSurface, [255, 255, 255], 0.04))} 0%, ${rgbToHex(bg)} 100%)`,
  };
}

function applyVars(vars) {
  Object.entries(vars).forEach(([name, value]) => {
    document.documentElement.style.setProperty(name, value);
  });
}

function loadImage(url, { crossOrigin = true } = {}) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (crossOrigin) {
      image.crossOrigin = 'anonymous';
      image.referrerPolicy = 'no-referrer';
    }
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load album art image'));
    image.src = url;
  });
}

async function makeImageFromCanvas(canvas) {
  const dataUrl = canvas.toDataURL('image/png');
  return loadImage(dataUrl, { crossOrigin: false });
}

async function buildSampleImages(image) {
  const sampleCanvas = document.createElement('canvas');
  sampleCanvas.width = 64;
  sampleCanvas.height = 64;
  const sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true });
  if (!sampleContext) return null;
  sampleContext.drawImage(image, 0, 0, 64, 64);

  const centerCanvas = document.createElement('canvas');
  centerCanvas.width = 64;
  centerCanvas.height = 64;
  const centerContext = centerCanvas.getContext('2d', { willReadFrequently: true });
  if (!centerContext) return null;
  const cropSize = 36;
  const sx = Math.floor((64 - cropSize) / 2);
  const sy = Math.floor((64 - cropSize) / 2);
  centerContext.drawImage(sampleCanvas, sx, sy, cropSize, cropSize, 0, 0, 64, 64);

  const [sampleImage, centerImage] = await Promise.all([
    makeImageFromCanvas(sampleCanvas),
    makeImageFromCanvas(centerCanvas),
  ]);
  return { sampleImage, centerImage };
}

async function computeFlexibleVars(albumArtUrl) {
  const image = await loadImage(albumArtUrl, { crossOrigin: true });
  const sourceWidth = image.naturalWidth || image.width || 0;
  const sourceHeight = image.naturalHeight || image.height || 0;
  if (!sourceWidth || !sourceHeight) {
    return buildFlexibleVars([120, 130, 145], [108, 127, 152], [77, 96, 120]);
  }

  const sampleImages = await buildSampleImages(image);
  if (!sampleImages) {
    return buildFlexibleVars([120, 130, 145], [108, 127, 152], [77, 96, 120]);
  }

  // Browser docs usage: instantiate and pass HTMLImageElement.
  const thief = new ColorThief();
  const palette = thief.getPalette(sampleImages.sampleImage, 8, 4) || [];
  const centerColor = thief.getColor(sampleImages.centerImage, 4) || palette[0] || [120, 130, 145];
  const { sideA, sideB } = pickSideColors(palette, centerColor);
  return buildFlexibleVars(centerColor, sideA, sideB);
}

export function useFlexibleAlbumPalette({ enabled, albumArtUrl, sampleImageUrl = '' }) {
  const [appliedVersion, setAppliedVersion] = useState(0);

  useEffect(() => {
    if (!enabled || !albumArtUrl) return undefined;
    const analysisUrl = sampleImageUrl || albumArtUrl;
    if (!analysisUrl) return undefined;

    let cancelled = false;
    const cached = FLEX_THEME_CACHE.get(analysisUrl);
    if (cached) {
      applyVars(cached);
      setAppliedVersion((version) => version + 1);
      return undefined;
    }

    (async () => {
      try {
        const vars = await computeFlexibleVars(analysisUrl);
        if (cancelled) return;
        FLEX_THEME_CACHE.set(analysisUrl, vars);
        applyVars(vars);
        setAppliedVersion((version) => version + 1);
      } catch (error) {
        console.warn('Flexible album palette generation failed for URL:', analysisUrl, error?.message || error);
        // fall back to currently applied palette values
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, albumArtUrl, sampleImageUrl]);

  return appliedVersion;
}
